// StorageAdapter — Default implementations for localStorage/sessionStorage
// Injected into VNInvestCharts to avoid direct global storage access
// All global access is lazy-initialized inside the factory functions for tree-shaking
// ============================================
// localStorage Adapter (persistent)
// ============================================
export const createLocalStorageAdapter = (prefix = 'vninvestcharts:') => {
    const isAvailable = () => {
        try {
            if (typeof localStorage === 'undefined')
                return false;
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        }
        catch {
            return false;
        }
    };
    // Lazy storage resolution
    let storage = null;
    const getStorage = () => {
        if (storage)
            return storage;
        storage = isAvailable() ? (typeof localStorage !== 'undefined' ? localStorage : createMemoryStorage()) : createMemoryStorage();
        return storage;
    };
    return {
        getItem: (key) => {
            try {
                return getStorage().getItem(`${prefix}${key}`);
            }
            catch {
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                getStorage().setItem(`${prefix}${key}`, value);
            }
            catch (e) {
                console.warn('StorageAdapter: setItem failed', e);
            }
        },
        removeItem: (key) => {
            try {
                getStorage().removeItem(`${prefix}${key}`);
            }
            catch {
                // ignore
            }
        },
        clear: () => {
            try {
                // Only clear keys with our prefix
                const keysToRemove = [];
                const store = getStorage();
                for (let i = 0; i < store.length; i++) {
                    const key = store.key(i);
                    if (key?.startsWith(prefix)) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => store.removeItem(key));
            }
            catch {
                // ignore
            }
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultLocalStorageAdapter = null;
export const getDefaultLocalStorageAdapter = () => {
    if (!_defaultLocalStorageAdapter) {
        _defaultLocalStorageAdapter = createLocalStorageAdapter();
    }
    return _defaultLocalStorageAdapter;
};
// Backward compatibility - lazy getter
export const defaultLocalStorageAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultLocalStorageAdapter();
        return adapter[prop];
    }
});
// ============================================
// sessionStorage Adapter (tab-scoped)
// ============================================
export const createSessionStorageAdapter = (prefix = 'vninvestcharts:') => {
    const isAvailable = () => {
        try {
            if (typeof sessionStorage === 'undefined')
                return false;
            const test = '__storage_test__';
            sessionStorage.setItem(test, test);
            sessionStorage.removeItem(test);
            return true;
        }
        catch {
            return false;
        }
    };
    // Lazy storage resolution
    let storage = null;
    const getStorage = () => {
        if (storage)
            return storage;
        storage = isAvailable() ? (typeof sessionStorage !== 'undefined' ? sessionStorage : createMemoryStorage()) : createMemoryStorage();
        return storage;
    };
    return {
        getItem: (key) => {
            try {
                return getStorage().getItem(`${prefix}${key}`);
            }
            catch {
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                getStorage().setItem(`${prefix}${key}`, value);
            }
            catch (e) {
                console.warn('StorageAdapter: setItem failed', e);
            }
        },
        removeItem: (key) => {
            try {
                getStorage().removeItem(`${prefix}${key}`);
            }
            catch {
                // ignore
            }
        },
        clear: () => {
            try {
                const keysToRemove = [];
                const store = getStorage();
                for (let i = 0; i < store.length; i++) {
                    const key = store.key(i);
                    if (key?.startsWith(prefix)) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => store.removeItem(key));
            }
            catch {
                // ignore
            }
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultSessionStorageAdapter = null;
export const getDefaultSessionStorageAdapter = () => {
    if (!_defaultSessionStorageAdapter) {
        _defaultSessionStorageAdapter = createSessionStorageAdapter();
    }
    return _defaultSessionStorageAdapter;
};
// Backward compatibility - lazy getter
export const defaultSessionStorageAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultSessionStorageAdapter();
        return adapter[prop];
    }
});
// ============================================
// Memory Storage (fallback for SSR/private browsing) - exported for testing
// ============================================
export function createMemoryStorage() {
    const store = new Map();
    return {
        get length() { return store.size; },
        key: (index) => Array.from(store.keys())[index] ?? null,
        getItem: (key) => store.get(key) ?? null,
        setItem: (key, value) => { store.set(key, value); },
        removeItem: (key) => { store.delete(key); },
        clear: () => { store.clear(); },
    };
}
export const createIndexedDBAdapter = (dbName = 'VNInvestCharts', storeName = 'workspaces') => {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not available'));
            return;
        }
        const request = indexedDB.open(dbName, 1);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const db = request.result;
            const adapter = {
                getItem: (key) => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readonly');
                        const store = tx.objectStore(storeName);
                        const req = store.get(key);
                        req.onsuccess = () => res(req.result?.value ?? null);
                        req.onerror = () => rej(req.error);
                    });
                },
                setItem: (key, value) => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readwrite');
                        const store = tx.objectStore(storeName);
                        store.put({ key, value, updatedAt: Date.now() });
                        tx.oncomplete = () => res();
                        tx.onerror = () => rej(tx.error);
                    });
                },
                removeItem: (key) => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readwrite');
                        const store = tx.objectStore(storeName);
                        store.delete(key);
                        tx.oncomplete = () => res();
                        tx.onerror = () => rej(tx.error);
                    });
                },
                clear: () => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readwrite');
                        const store = tx.objectStore(storeName);
                        store.clear();
                        tx.oncomplete = () => res();
                        tx.onerror = () => rej(tx.error);
                    });
                },
                getAllKeys: () => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readonly');
                        const store = tx.objectStore(storeName);
                        const req = store.getAllKeys();
                        req.onsuccess = () => res(req.result.map(k => String(k)));
                        req.onerror = () => rej(req.error);
                    });
                },
                getSize: () => {
                    return new Promise((res, rej) => {
                        const tx = db.transaction(storeName, 'readonly');
                        const store = tx.objectStore(storeName);
                        const req = store.count();
                        req.onsuccess = () => res(req.result);
                        req.onerror = () => rej(req.error);
                    });
                },
            };
            resolve(adapter);
        };
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(storeName)) {
                db.createObjectStore(storeName, { keyPath: 'key' });
            }
        };
    });
};
export const createCompositeStorageAdapter = async (thresholdBytes = 1024 * 1024 // 1MB default
) => {
    const localAdapter = createLocalStorageAdapter();
    const indexedDBAdapter = await createIndexedDBAdapter().catch(() => null);
    let currentThreshold = thresholdBytes;
    // Async version for internal use
    const asyncAdapter = {
        getItem: async (key) => {
            const local = localAdapter.getItem(key);
            if (local !== null)
                return local;
            if (indexedDBAdapter) {
                return indexedDBAdapter.getItem(key);
            }
            return null;
        },
        setItem: async (key, value) => {
            const size = new Blob([value]).size;
            if (size < currentThreshold) {
                localAdapter.setItem(key, value);
            }
            else if (indexedDBAdapter) {
                await indexedDBAdapter.setItem(key, value);
            }
            else {
                localAdapter.setItem(key, value);
            }
        },
        removeItem: async (key) => {
            localAdapter.removeItem(key);
            await indexedDBAdapter?.removeItem(key);
        },
        clear: async () => {
            localAdapter.clear();
            await indexedDBAdapter?.clear();
        },
    };
    // Sync interface (for StorageAdapter compatibility)
    return {
        getItem: (key) => {
            const local = localAdapter.getItem(key);
            if (local !== null)
                return local;
            // For sync, we can't await IndexedDB. Return null.
            return null;
        },
        setItem: (key, value) => {
            const size = new Blob([value]).size;
            if (size < currentThreshold) {
                localAdapter.setItem(key, value);
            }
            else if (indexedDBAdapter) {
                // Fire and forget for sync interface
                indexedDBAdapter.setItem(key, value).catch(() => { });
            }
            else {
                localAdapter.setItem(key, value);
            }
        },
        removeItem: (key) => {
            localAdapter.removeItem(key);
            indexedDBAdapter?.removeItem(key).catch(() => { });
        },
        clear: () => {
            localAdapter.clear();
            indexedDBAdapter?.clear().catch(() => { });
        },
        setThreshold: (bytes) => {
            currentThreshold = bytes;
        },
    };
};
